import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";

export function DomainPricingSection() {
  const [selectedTab, setSelectedTab] = useState("popular");

  const domainCategories = [
    { id: "popular", name: "Popular" },
    { id: "sale", name: "On Sale" },
    { id: "tech", name: "AI & Tech" },
    { id: "business", name: "Business" },
  ];

  const domainPricing = {
    popular: [
      { tld: ".com", price: 5.98, regularPrice: 13.98, sale: true },
      { tld: ".net", price: 8.98, regularPrice: 15.98, sale: true },
      { tld: ".org", price: 7.98, regularPrice: 12.98, sale: true },
      { tld: ".co", price: 11.98, regularPrice: 24.98, sale: true },
      { tld: ".io", price: 32.98, regularPrice: null, sale: false },
      { tld: ".dev", price: 14.98, regularPrice: null, sale: false },
    ],
    sale: [
      { tld: ".com", price: 5.98, regularPrice: 13.98, sale: true },
      { tld: ".net", price: 8.98, regularPrice: 15.98, sale: true },
      { tld: ".org", price: 7.98, regularPrice: 12.98, sale: true },
      { tld: ".co", price: 11.98, regularPrice: 24.98, sale: true },
      { tld: ".site", price: 1.98, regularPrice: 9.98, sale: true },
      { tld: ".online", price: 2.98, regularPrice: 19.98, sale: true },
    ],
    tech: [
      { tld: ".ai", price: 69.98, regularPrice: null, sale: false },
      { tld: ".app", price: 12.98, regularPrice: null, sale: false },
      { tld: ".dev", price: 14.98, regularPrice: null, sale: false },
      { tld: ".tech", price: 4.98, regularPrice: 24.98, sale: true },
      { tld: ".io", price: 32.98, regularPrice: null, sale: false },
      { tld: ".cloud", price: 9.98, regularPrice: 19.98, sale: true },
    ],
    business: [
      { tld: ".com", price: 5.98, regularPrice: 13.98, sale: true },
      { tld: ".biz", price: 7.98, regularPrice: 14.98, sale: true },
      { tld: ".co", price: 11.98, regularPrice: 24.98, sale: true },
      { tld: ".store", price: 3.98, regularPrice: 24.98, sale: true },
      { tld: ".shop", price: 4.98, regularPrice: 19.98, sale: true },
      { tld: ".company", price: 6.98, regularPrice: 14.98, sale: true },
    ],
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-16 bg-muted/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Domain Name Pricing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Find the perfect domain extension for your website with our competitive pricing. 
            Special offers available for a limited time.
          </p>
        </div>

        <div className="mb-8">
          <Tabs defaultValue="popular" value={selectedTab} onValueChange={setSelectedTab} className="w-full">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-transparent">
              {domainCategories.map((category) => (
                <TabsTrigger 
                  key={category.id} 
                  value={category.id}
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {Object.keys(domainPricing).map((categoryId) => (
              <TabsContent key={categoryId} value={categoryId} className="mt-6">
                <motion.div 
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
                  variants={container}
                  initial="hidden"
                  animate={selectedTab === categoryId ? "show" : "hidden"}
                >
                  {domainPricing[categoryId as keyof typeof domainPricing].map((domain, index) => (
                    <motion.div key={domain.tld} variants={item}>
                      <Card className="overflow-hidden domain-card h-full">
                        <CardContent className="p-4 flex flex-col h-full">
                          <div className="text-2xl font-bold text-foreground mb-1">
                            {domain.tld}
                          </div>
                          <div className="mb-auto">
                            {domain.sale && (
                              <span className="text-xs text-primary font-semibold uppercase bg-primary/10 px-2 py-0.5 rounded-sm">
                                Sale
                              </span>
                            )}
                          </div>
                          <div className="mt-2">
                            <div className="text-lg font-bold text-foreground">
                              ${domain.price}/yr
                            </div>
                            {domain.regularPrice && (
                              <div className="text-xs text-muted-foreground line-through">
                                ${domain.regularPrice}/yr
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <div className="text-center mt-8">
          <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Link to="/pricing">View Full Pricing</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}